import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from '../../plugins/axios';

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

export interface HiscoxMember {
    policyNumber: string;
    startDate: string;
    endDate: string;
    endorsementDate: string;
    status: string;
    name: string;
    postCode: string;
    channel: string;
    brokerCompanyName: string;
    brokerNumber?: string;
    brokerContact?: string;
}

export interface Submission {
    id: number;
    createdOn: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    mobile?: string;
    policyNumber: string;
    broker: string;
    companyName: string;
    addressLine1: string;
    addressLine2?: string;
    addressLine3?: string;
    city: string;
    postCode: string;
}

interface RegistrationState {
    fetchingHiscoxMembers: boolean;
    hiscoxMembers: HiscoxMember[];
    fetchingSubmissions: boolean;
    submissions: Submission[];
}

const initialState: RegistrationState = {
    fetchingHiscoxMembers: false,
    hiscoxMembers: [],
    fetchingSubmissions: false,
    submissions: [],
};

export const fetchHiscoxMembers = createAsyncThunk<HiscoxMember[]>(
    'registration/fetchHiscoxMembers',
    async () => {
        const response = await axios.get(`${apiUrl}/registration/hiscoxmembers`);
        return response.data;
    },
);

export const fetchSubmissions = createAsyncThunk<Submission[]>(
    'registration/fetchRegistrations',
    async () => {
        const response = await axios.get(`${apiUrl}/registration/submissions`);
        return response.data;
    },
);

export const dismissSubmission = createAsyncThunk<number, number>(
    'registration/dismissSubmission',
    async (id) => {
        await axios.post(`${apiUrl}/registration/dismiss/${id}`);
        return id;
    },
);

export const registrationSlice = createSlice({
    name: 'registrations',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchHiscoxMembers.pending, (state) => {
                state.fetchingHiscoxMembers = true;
            })
            .addCase(fetchHiscoxMembers.fulfilled, (state, action: PayloadAction<HiscoxMember[]>) => {
                state.hiscoxMembers = action.payload;
                state.fetchingHiscoxMembers = false;
            })
            .addCase(fetchHiscoxMembers.rejected, (state) => {
                state.fetchingHiscoxMembers = false;
            })
            .addCase(fetchSubmissions.pending, (state) => {
                state.fetchingSubmissions = true;
            })
            .addCase(fetchSubmissions.fulfilled, (state, action: PayloadAction<Submission[]>) => {
                state.submissions = action.payload;
                state.fetchingSubmissions = false;
            })
            .addCase(fetchSubmissions.rejected, (state) => {
                state.fetchingSubmissions = false;
            })
            .addCase(dismissSubmission.pending, (state) => {
                state.fetchingSubmissions = true;
            })
            .addCase(dismissSubmission.fulfilled, (state, action: PayloadAction<number>) => {
                state.submissions = state.submissions.filter((s) => s.id !== action.payload);
                state.fetchingSubmissions = false;
            })
            .addCase(dismissSubmission.rejected, (state) => {
                state.fetchingSubmissions = false;
            });
    },
});

export default registrationSlice.reducer;
